import api from "./api";
import { toast } from "sonner";

/**
 * Triggers an authenticated PDF download in the browser.
 *
 * Why a helper instead of an `<a href=…>`?
 *  - The endpoints require an `Authorization: Bearer …` header. Plain anchor
 *    tags can't send headers, so we fetch the binary, build a blob URL, and
 *    drive an offscreen `<a>` to fire the download.
 *  - Works identically on mobile Safari / Chrome and on desktop.
 *
 * @param {string} url     API path (e.g. "/ride-requests/123/quote-pdf")
 * @param {string} filename  Suggested filename for the saved file
 */
export async function downloadPdf(url, filename) {
  try {
    const { data, headers } = await api.get(url, { responseType: "blob" });

    // Server may suggest its own filename via Content-Disposition; respect it
    // when the caller didn't pass an explicit one.
    let resolved = filename;
    const cd = headers?.["content-disposition"] || "";
    const m = cd.match(/filename="?([^";]+)"?/i);
    if (!resolved && m) resolved = m[1];
    if (!resolved) resolved = "document.pdf";

    const blob = new Blob([data], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = resolved;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Give Safari a tick to start the download before we revoke
    setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
    return true;
  } catch (e) {
    const detail = e?.response?.data;
    // The blob response may have wrapped the JSON error body — try to surface it.
    if (detail instanceof Blob) {
      try {
        const text = await detail.text();
        const parsed = JSON.parse(text);
        toast.error(parsed.detail || "Téléchargement impossible");
      } catch {
        toast.error("Téléchargement impossible");
      }
    } else {
      toast.error(detail?.detail || "Téléchargement impossible");
    }
    return false;
  }
}
